import * as React from 'react';
import { GitBranch, X } from 'lucide-react';
import {
  AutosaveField,
  PanelSection,
  SidePanel,
  WorkspaceButton,
  WorkspaceNotice,
  useToast,
} from '@/components/ui/workspace';
import { useLibraryTreeQuery } from '@/lib/api/requirement-library';
import { draftApi, type AreaPatch, type FrameworkArea } from '@/lib/api/discovery-frameworks';
import { cn } from '@/lib/utils/cn';
import { tryAdd, type EditorCtx } from './editor';
import { isActive, messageOf, shown } from './framework-logic';
import { ChangeTag, FieldHelp, InlineAdd, QuestionList, SaveStatus } from './parts';

/**
 * Side panel for one area (prototype `fwDomainPanel()`): short label, full
 * name, definition of complete, processes to map, requirement library
 * modules, the area's own questions and the interview types that cover it.
 * In edit mode everything saves to the draft as you type.
 *
 * Reference processes: a process with a reference map opens it in the map
 * editor (T-0189), in place of the areas; in a draft, one without can be
 * drawn there.
 */

export const P_TEXT = 'text-body text-ink-900';
export const P_BOX =
  'rounded-card border border-ink-100 bg-paper-50 px-3 py-2.5 text-body-sm text-ink-700';
const CHIP =
  'inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded-full border border-ink-100 bg-paper-100 px-2.5 text-meta font-medium text-ink-700';
const ICON_BUTTON =
  'inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-chip text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600';

export function AreaPanel({
  ctx,
  area,
  onClose,
  onOpenProcess,
}: {
  ctx: EditorCtx;
  area: FrameworkArea;
  onClose: () => void;
  onOpenProcess?: (processId: string) => void;
}) {
  const { content, editing, busy, write } = ctx;
  const { toast } = useToast();
  const retired = area.status === 'retired';
  const canEdit = editing && !retired;

  const patch = (p: AreaPatch) =>
    write((id, base) => draftApi.patchArea(id, base, area.id, p), { what: area.code });
  const questions = shown(content.area_questions[area.id] ?? [], editing);
  const processes = shown(
    content.processes.filter((p) => p.area_id === area.id),
    editing,
  );
  const covering = content.interview_types.filter(
    (t) => isActive(t) && t.area_ids.includes(area.id),
  );

  return (
    <SidePanel
      aria-label={`Area ${area.code}`}
      identifier={area.code}
      path={<span className="text-body-sm font-semibold text-ink-900">{area.label}</span>}
      onClose={onClose}
      footer={
        editing ? (
          <>
            <SaveStatus busy={busy} />
            {retired ? (
              <WorkspaceButton disabled={busy} onClick={() => void patch({ status: 'active' })}>
                Restore area
              </WorkspaceButton>
            ) : (
              <WorkspaceButton
                variant="destructive"
                disabled={busy}
                onClick={() => void patch({ status: 'retired' })}
              >
                Retire area
              </WorkspaceButton>
            )}
          </>
        ) : undefined
      }
    >
      {retired ? (
        <WorkspaceNotice>
          Retired. Engagements moved to the next version no longer cover it. Restore it to change
          it.
        </WorkspaceNotice>
      ) : null}
      <PanelSection label="Short label">
        {canEdit ? (
          <AutosaveField
            required
            aria-label="Short label"
            value={area.label}
            maxLength={80}
            onSave={(label) => void patch({ label })}
          />
        ) : (
          <p className={P_TEXT}>{area.label}</p>
        )}
      </PanelSection>
      <PanelSection label="Full name">
        {canEdit ? (
          <AutosaveField
            required
            aria-label="Full name"
            value={area.name}
            maxLength={200}
            onSave={(name) => void patch({ name })}
          />
        ) : (
          <p className={P_TEXT}>{area.name}</p>
        )}
      </PanelSection>
      <PanelSection label="Definition of complete">
        {canEdit ? (
          <AutosaveField
            multiline
            rows={5}
            aria-label="Definition of complete"
            value={area.definition}
            maxLength={4000}
            onSave={(definition) => void patch({ definition })}
          />
        ) : (
          <div className={P_BOX}>{area.definition || 'Not written yet.'}</div>
        )}
        <FieldHelp>
          The bar a consultant checks before calling this area done. Shown on every
          engagement&rsquo;s coverage screen.
        </FieldHelp>
      </PanelSection>

      <PanelSection label="Processes to map">
        {processes.length > 0 ? (
          <ul>
            {processes.map((p) => (
              <li
                key={p.id}
                className="flex items-center gap-2.5 border-b border-ink-100 py-[7px] text-body-sm text-ink-900 last:border-b-0"
              >
                <GitBranch className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
                <span
                  className={cn(
                    'min-w-0 flex-1',
                    p.status === 'retired' && 'text-ink-300 line-through',
                  )}
                >
                  {p.name} <ChangeTag change={p.change} />
                </span>
                {onOpenProcess && p.status !== 'retired' && (p.has_reference_map || canEdit) ? (
                  <button
                    type="button"
                    className="flex-none text-meta font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900"
                    onClick={() => onOpenProcess(p.id)}
                  >
                    {p.has_reference_map ? 'Reference process' : 'Draw a reference process'}
                  </button>
                ) : (
                  <span className="flex-none text-meta text-ink-500">
                    {p.has_reference_map ? 'Reference process' : 'No reference yet'}
                  </span>
                )}
                {canEdit ? (
                  p.status === 'retired' ? (
                    <WorkspaceButton
                      variant="tertiary"
                      disabled={busy}
                      onClick={() =>
                        void write((id, base) => draftApi.patchProcess(id, base, p.id, 'active'), {
                          what: p.name,
                        })
                      }
                    >
                      Restore
                    </WorkspaceButton>
                  ) : (
                    <button
                      type="button"
                      aria-label={`Retire process ${p.name}`}
                      title="Retire"
                      disabled={busy}
                      className={ICON_BUTTON}
                      onClick={() =>
                        void write((id, base) => draftApi.patchProcess(id, base, p.id, 'retired'), {
                          what: p.name,
                        })
                      }
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-meta text-ink-500">None</p>
        )}
        {canEdit ? (
          <div className="mt-1.5">
            <InlineAdd
              trigger="chip"
              label="Process"
              inputLabel="New process name"
              placeholder="Process name, e.g. Vendor setup"
              busy={busy}
              onAdd={async (name) => {
                const { refusal } = await tryAdd(
                  write,
                  (id, base) => draftApi.addProcess(id, base, area.id, name),
                  name,
                  (err) => messageOf(err, 'The process could not be added.'),
                );
                if (!refusal) toast({ message: `"${name}" added to the draft.` });
                return refusal;
              }}
            />
          </div>
        ) : null}
        <FieldHelp>
          AI uses a reference process as the skeleton; reference steps nobody describes show as
          unconfirmed. Open one to see it and change it, by hand or by chat.
        </FieldHelp>
      </PanelSection>

      <PanelSection label="Requirement library modules">
        <div className="flex flex-wrap items-center gap-1.5">
          {area.library_modules.map((m) => (
            <span key={m.id} className={CHIP}>
              {m.name ?? 'Unknown module'}
              {m.status === 'retired' ? ' (retired)' : ''}
              {canEdit ? (
                <button
                  type="button"
                  aria-label={`Remove ${m.name ?? 'module'}`}
                  disabled={busy}
                  className="-mr-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                  onClick={() =>
                    void patch({
                      library_module_ids: area.library_module_ids.filter((x) => x !== m.id),
                    })
                  }
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              ) : null}
            </span>
          ))}
          {area.library_modules.length === 0 && !canEdit ? (
            <span className="text-meta text-ink-500">None</span>
          ) : null}
          {canEdit ? (
            <ModulePicker
              linked={area.library_module_ids}
              disabled={busy}
              onPick={(moduleId) =>
                void patch({ library_module_ids: [...area.library_module_ids, moduleId] })
              }
            />
          ) : null}
        </div>
      </PanelSection>

      <PanelSection label="Questions for this area">
        <QuestionList
          items={questions}
          editing={canEdit}
          busy={busy}
          kind="question"
          addLabel="Question"
          onSave={(q, text) =>
            void write((id, base) => draftApi.patchQuestion(id, base, q.id, { text }), {
              what: q.code,
            })
          }
          onStatus={(q, status) =>
            void write((id, base) => draftApi.patchQuestion(id, base, q.id, { status }), {
              what: q.code,
            })
          }
          onAdd={async (text) => {
            const { result, refusal } = await tryAdd(
              write,
              (id, base) => draftApi.addAreaQuestion(id, base, area.id, text),
              area.code,
              (err) => messageOf(err, 'The question could not be added.'),
            );
            if (result?.code) toast({ message: `${result.code} added to the draft.` });
            return refusal;
          }}
        />
        <FieldHelp>Used in every interview that covers this area, and in follow-ups.</FieldHelp>
      </PanelSection>

      <PanelSection label="Interview types that cover it">
        <p className="text-body-sm text-ink-900">
          {covering.map((t) => t.name).join(', ') || 'None'}
        </p>
      </PanelSection>
    </SidePanel>
  );
}

/** "+ Module": the requirement library's active modules, by category. */
function ModulePicker({
  linked,
  disabled,
  onPick,
}: {
  linked: readonly string[];
  disabled: boolean;
  onPick: (moduleId: string) => void;
}) {
  const tree = useLibraryTreeQuery().data;
  const categories = (tree?.categories ?? [])
    .map((c) => ({
      ...c,
      modules: c.modules.filter((m) => m.status === 'active' && !linked.includes(m.id)),
    }))
    .filter((c) => c.modules.length > 0);
  return (
    <select
      aria-label="Add a requirement library module"
      value=""
      disabled={disabled || !tree}
      onChange={(e) => {
        if (e.target.value) onPick(e.target.value);
      }}
      className="h-[22px] cursor-pointer rounded-full border border-dashed border-ink-200 bg-paper-0 px-2 text-[11px] font-medium text-ink-500 hover:border-ink-300 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
    >
      <option value="">+ Module</option>
      {categories.map((c) => (
        <optgroup key={c.id} label={c.name}>
          {c.modules.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
